import { FC, useMemo } from "react";
import { Button } from "@radix-ui/themes";
import { ColumnDef } from "@tanstack/react-table";
import { Table } from "@/components/table/table";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { TextFieldRow } from "@/components/table/text-field-row";
import { orderBy } from "lodash-es";
import { DateTime } from "luxon";

interface ImportSessionSchema {
  id: string;
  owned_by_space_account_id: string;
  title?: string | null;
  status?: string | null;
  kind?: string | null;
  internal_created_at?: string | null;
}

interface ImportSessionsTableProps {
  data: ImportSessionSchema[];
  onViewImportSession: (id: string) => void;
  pageSize: number;
}

export const ImportSessionsTable: FC<ImportSessionsTableProps> = ({
  data,
  onViewImportSession,
  pageSize,
}) => {
  const columns = useMemo<ColumnDef<ImportSessionSchema>[]>(
    () => [
      {
        header: "ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.id} />,
      },
      {
        header: "Space Account Id",
        cell: ({ row }) => <UuidFieldRow id={row.original.owned_by_space_account_id} />,
      },
      {
        header: "Title",
        cell: ({ row }) => <TextFieldRow value={row.original.title ?? ""} />,
      },
      {
        header: "Status",
        cell: ({ row }) => <TextFieldRow value={row.original.status ?? ""} />,
      },
      {
        header: "Created",
        cell: ({ row }) => {
          const relative = row.original.internal_created_at
            ? (DateTime.fromISO(row.original.internal_created_at).toRelative({ locale: "en" }) ??
              "")
            : "";
          return <TextFieldRow value={relative} />;
        },
      },
      {
        header: "View",
        cell: ({ row }) => (
          <Button variant="outline" size="1" onClick={() => onViewImportSession(row.original.id)}>
            View
          </Button>
        ),
      },
    ],
    [onViewImportSession]
  );

  const orderedData = useMemo(
    () => orderBy(data, x => x.internal_created_at ?? "", "desc"),
    [data]
  );

  return <Table data={orderedData} columns={columns} pageSize={pageSize} />;
};
